'use client';

import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import type { Candidate as SearchCandidate } from "@/types/search";
import type { Candidate as JobCandidate } from "@/types/candidate";

// Create a union type that can handle both candidate types
type FlexibleCandidate = SearchCandidate | JobCandidate;

// Interface for export data stored in localStorage (lightweight - only referral codes)
export interface ExportCandidate {
  referralCode: string;
  query: string;
  timestamp: string;
  source: 'search' | 'job';
  jobId?: string;
}

interface ExportContextType {
  // Export mode state
  isExportMode: boolean;
  toggleExportMode: () => void;
  
  // Export candidates management
  exportCandidates: ExportCandidate[];
  addCandidateToExport: (candidate: FlexibleCandidate, query: string, source: 'search' | 'job', jobId?: string) => void;
  addAllCandidatesToExport: (candidates: FlexibleCandidate[], query: string, source: 'search' | 'job', jobId?: string) => void;
  removeCandidateFromExport: (referralCode: string) => void;
  removeAllCandidatesFromExport: (referralCodes: string[]) => void;
  clearExportCandidates: () => void;
  clearAllExportState: () => void; // Clears both candidates and turns off export mode
  
  // Export modal state
  isExportModalOpen: boolean;
  openExportModal: () => void;
  closeExportModal: () => void;
  
  // Query tracking
  getExportByQuery: () => Record<string, ExportCandidate[]>;
  getTotalExportCount: () => number;
}

const ExportContext = createContext<ExportContextType | undefined>(undefined);

const EXPORT_STORAGE_KEY = 'jedi-export-candidates';

export function ExportProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  
  // Export mode state
  const [isExportMode, setIsExportMode] = useState(false);
  const [exportCandidates, setExportCandidates] = useState<ExportCandidate[]>([]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Load export data from localStorage on mount
  useEffect(() => {
    try {
      // Check if localStorage is available and accessible
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem(EXPORT_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as ExportCandidate[];
          setExportCandidates(parsed);
        }
      }
    } catch (error) {
      // Silently fail if localStorage is not accessible (e.g., private browsing, restricted contexts)
      console.error('Failed to load export data from localStorage:', error);
    }
  }, []);

  // Save to localStorage whenever exportCandidates changes
  useEffect(() => {
    try {
      // Check if localStorage is available and accessible
      if (typeof window !== 'undefined' && window.localStorage) {
        if (exportCandidates.length === 0) {
          // If no candidates, remove from localStorage instead of storing empty array
          localStorage.removeItem(EXPORT_STORAGE_KEY);
        } else {
          // Only store non-empty arrays
          localStorage.setItem(EXPORT_STORAGE_KEY, JSON.stringify(exportCandidates));
        }
      }
    } catch (error) {
      // Silently fail if localStorage is not accessible
      console.error('Failed to save export data to localStorage:', error);
    }
  }, [exportCandidates]);

  const toggleExportMode = useCallback(() => {
    setIsExportMode(prev => !prev);
    // Don't auto-open modal - let user manually open it when needed
  }, []);

  const addCandidateToExport = useCallback((
    candidate: FlexibleCandidate, 
    query: string, 
    source: 'search' | 'job',
    jobId?: string
  ) => {
    // Only store referral code, not entire candidate object
    if (!candidate.referralCode) {
      toast({
        description: `Cannot export ${candidate.name} - no referral code found`,
        variant: 'error',
      });
      return;
    }

    const exportCandidate: ExportCandidate = {
      referralCode: candidate.referralCode,
      query,
      timestamp: new Date().toISOString(),
      source,
      jobId
    };

    setExportCandidates(prev => {
      // Check if candidate already exists
      const exists = prev.some(item => item.referralCode === candidate.referralCode);
      if (exists) {
        // Update existing entry with new query/timestamp
        return prev.map(item => 
          item.referralCode === candidate.referralCode ? exportCandidate : item
        );
      } else {
        // Add new candidate
        return [...prev, exportCandidate];
      }
    });

    toast({
      description: `Added ${candidate.name} to export list`,
    });
  }, [toast]);

  const addAllCandidatesToExport = useCallback((
    candidates: FlexibleCandidate[], 
    query: string, 
    source: 'search' | 'job',
    jobId?: string
  ) => {
    const timestamp = new Date().toISOString();
    
    // Only process candidates with referral codes
    const validCandidates = candidates.filter(candidate => candidate.referralCode);
    
    if (validCandidates.length === 0) {
      toast({
        description: 'No candidates with referral codes found to export',
        variant: 'error',
      });
      return;
    }

    const newExportCandidates: ExportCandidate[] = validCandidates.map(candidate => ({
      referralCode: candidate.referralCode!,
      query,
      timestamp,
      source,
      jobId
    }));

    setExportCandidates(prev => {
      const existingRefs = new Set(prev.map(item => item.referralCode));
      const candidatesToAdd = newExportCandidates.filter(item => !existingRefs.has(item.referralCode));
      return [...prev, ...candidatesToAdd];
    });

    toast({
      description: `Added ${validCandidates.length} candidates to export list`,
    });
  }, [toast]);

  const removeCandidateFromExport = useCallback((referralCode: string) => {
    setExportCandidates(prev => {
      const updated = prev.filter(item => item.referralCode !== referralCode);
      const removedCount = prev.length - updated.length;
      
      if (removedCount > 0) {
        toast({
          description: `Removed candidate from export list`,
        });
      }
      
      return updated;
    });
  }, [toast]);

    const removeAllCandidatesFromExport = useCallback((referralCodes: string[]) => {
    setExportCandidates(prev => {
      const updated = prev.filter(item => !referralCodes.includes(item.referralCode));
      const removedCount = prev.length - updated.length;

      if (removedCount > 0) {
        toast({
          description: `Removed ${removedCount} candidate${removedCount !== 1 ? 's' : ''} from export list`,
        });
      }

      return updated;
    });
  }, [toast]);

  const clearExportCandidates = useCallback(() => {
    setExportCandidates([]);
    // Also clear from localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(EXPORT_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to clear export data from localStorage:', error);
    }
    toast({
      description: 'Cleared all export candidates',
    });
  }, [toast]);

  const clearAllExportState = useCallback(() => {
    setExportCandidates([]);
    setIsExportMode(false);
    setIsExportModalOpen(false);
    // Also clear from localStorage
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(EXPORT_STORAGE_KEY);
      }
    } catch (error) {
      console.error('Failed to clear export data from localStorage:', error);
    }
  }, []);

  const openExportModal = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const closeExportModal = useCallback(() => {
    setIsExportModalOpen(false);
  }, []);

  const getExportByQuery = useCallback(() => {
    return exportCandidates.reduce((acc, item) => {
      const key = item.query || 'Unknown Query';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {} as Record<string, ExportCandidate[]>);
  }, [exportCandidates]);

  const getTotalExportCount = useCallback(() => {
    return exportCandidates.length;
  }, [exportCandidates.length]);

  return (
    <ExportContext.Provider
      value={{
        isExportMode,
        toggleExportMode,
        exportCandidates,
        addCandidateToExport,
        addAllCandidatesToExport,
        removeCandidateFromExport,
        removeAllCandidatesFromExport,
        clearExportCandidates,
        clearAllExportState,
        isExportModalOpen,
        openExportModal,
        closeExportModal,
        getExportByQuery,
        getTotalExportCount,
      }}
    >
      {children}
    </ExportContext.Provider>
  );
}

export function useExport() {
  const context = useContext(ExportContext);
  if (!context) {
    throw new Error('useExport must be used within ExportProvider');
  }
  return context;
}